import {Subject} from 'rxjs';
import {switchMap} from 'rxjs/operators';

import * as moment from 'moment';

import {
    ServerResponse,
    HttpStatusCodesEnum,
    DocumentStatusesEnum,
    AuthApiService,
    CompanyDocumentInterface,
    IndividualDocumentInterface,
    CompanyExtendedModel,
    CompanyDocumentModel,
    IndividualExtendedModel,
    IndividualDocumentModel,
    ApiError,
    ApiResponse,
    CompanyDocumentsAtomicService,
    CreateIndividualDocumentAndFilesRequestModel,
    IndividualDocumentsAtomicService,
    CreateIndividualDocumentAndFilesInterface,
    CompanyDocumentAndFilesModel,
    CompanyDocumentAndFilesInterface
} from '@spa/api';

import {Helper} from '../../../../shared/classes';

import { CompanyDocumentsFormErrorStatesEnum } from 'appEntries/panel/enums';

export class OtherDocumentsForm {
    private readonly isCorporateClient: boolean;
    private readonly authApiService: AuthApiService;
    private readonly client: IndividualExtendedModel | CompanyExtendedModel;
    private readonly companyDocumentsAtomicService?: CompanyDocumentsAtomicService;
    private readonly individualDocumentsAtomicService?: IndividualDocumentsAtomicService;

    public document: IndividualDocumentModel | CompanyDocumentModel;
    public documentSubject: Subject<string>;
    public documentErrorsSubject: Subject<string[]>;
    public documentFile: File;
    public initialDocumentType: string;
    public reviewSummary: string;
    public hiddenDocument: boolean;

    constructor(
        isCorporateClient: boolean,
        client: IndividualExtendedModel | CompanyExtendedModel,
        authApiService?: AuthApiService,
        companyDocumentsAtomicService?: CompanyDocumentsAtomicService,
        individualDocumentsAtomicService?: IndividualDocumentsAtomicService
    ) {
        this.client = client;
        this.isCorporateClient = isCorporateClient;
        this.authApiService = authApiService;
        this.companyDocumentsAtomicService = companyDocumentsAtomicService;
        this.individualDocumentsAtomicService = individualDocumentsAtomicService;

        this.documentSubject = new Subject();
        this.documentErrorsSubject = new Subject();
        this.documentFile = null;
        this.initialDocumentType = null;
        this.document = null;
        this.reviewSummary = '';
        this.hiddenDocument = false;
    }

    private registerIndividualDocumentWithAtomicService(file: File, documentType: string): ServerResponse<IndividualDocumentModel> {

        const model: CreateIndividualDocumentAndFilesInterface = {
            individual_id: (this.client as IndividualExtendedModel).id,
            document_type: documentType,
            sub_document_type: null,
            files: [this.documentFile],
            is_hidden_for_client: this.hiddenDocument,
            upload_stamp: moment().utc().toISOString(),
            uploaded_by: this.authApiService.getAuthenticatedUser().id,
            review_summary: this.reviewSummary,
            poi_document_id: null,
            file_name: file.name
        };
        const createRequestModel = new CreateIndividualDocumentAndFilesRequestModel(model);

        return this.individualDocumentsAtomicService.AtomicCreateFile(createRequestModel)
            .pipe(
                switchMap((response: ApiResponse<ApiError[] | IndividualDocumentInterface>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponseObservable<IndividualDocumentInterface>(response);
                    }

                    const data = response.data as IndividualDocumentInterface;
                    const responseModel = new IndividualDocumentModel(data);

                    (this.client as IndividualExtendedModel).documents.push(responseModel);

                    this.documentSubject.next(data.file_storage_id[0]);
                    this.documentFile = null;

                    return Helper.createSuccessResponseObservable<IndividualDocumentModel>(responseModel);
                }));

    }

    private registerDocumentWithAtomicService(file: File, documentType: string): ServerResponse<CompanyDocumentModel> {
        const companyDocumentUploadModel: CompanyDocumentAndFilesInterface = {
            documentType,
            companyId: this.client.id,
            fileName: file.name,
            subDocumentType: '',
            uploadedBy: this.authApiService.getAuthenticatedUser().id,
            reviewSummary: this.reviewSummary,
            isHiddenForClient: this.hiddenDocument,
            files: [file],
            fileStorageIds: ['']
        };

        const createCompanyDocumentAndFilesModel = new CompanyDocumentAndFilesModel(
            companyDocumentUploadModel
        );

        return this.companyDocumentsAtomicService.atomicCreateAndRegister(createCompanyDocumentAndFilesModel)
            .pipe(
                switchMap((response: ApiResponse<ApiError[] | CompanyDocumentInterface>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponseObservable<CompanyDocumentInterface>(response,
                            CompanyDocumentsFormErrorStatesEnum.DocumentRegistrationFailed);
                    }

                    const data = response.data as CompanyDocumentInterface;
                    const model = new CompanyDocumentModel(data);

                    if (!this.client.documents) {
                        this.client.documents = [];
                    }

                    this.documentSubject.next(model.fileStorageIds[0]);
                    this.documentFile = null;

                    return Helper.createSuccessResponseObservable<CompanyDocumentModel>(model);
                }));
    }

    resetDocument(): void {
        this.document = null;
        this.documentSubject.next(null);
        this.documentErrorsSubject.next([]);
    }

    initDocument(documents = [], documentType: string): void {
        const statuses = [
            DocumentStatusesEnum.New,
            DocumentStatusesEnum.InReview,
            DocumentStatusesEnum.Approved
        ] as number[];

        const filteredDocuments = documents
            .filter((document: IndividualDocumentModel | CompanyDocumentModel) => {
                return (
                    (document.documentType === documentType) &&
                    statuses.includes(document.status)
                );
            })
            .sort((
                current: IndividualDocumentModel | CompanyDocumentModel,
                next: IndividualDocumentModel | CompanyDocumentModel
            ) => {
                return moment(next.uploadStamp).unix() - moment(current.uploadStamp).unix();
            });

        if (filteredDocuments[0]) {
            this.document = filteredDocuments[0];

            this.initialDocumentType = filteredDocuments[0].documentType;
        }

        setTimeout(() => {
            if (filteredDocuments[0]) {
                this.documentSubject.next(filteredDocuments[0].fileStorageIds[0]);
            }
        }, 0);
    }

    validateDocumentFile(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        const documentSubjectErrors = Helper.validateFile(this.documentFile, messageFunction);

        this.documentErrorsSubject.next(documentSubjectErrors);

        return (documentSubjectErrors.length === 0);
    }

    createRequest(documentType: string): ServerResponse<IndividualDocumentModel | CompanyDocumentModel> | null {
        if (
            !this.initialDocumentType ||
            (this.document && (this.document.fileStorageIds[0] !== this.documentFile.name))
        ) {
            if (!this.initialDocumentType) {
                if (this.isCorporateClient) {

                    return this.registerDocumentWithAtomicService(this.documentFile, documentType);
                }

                return this.registerIndividualDocumentWithAtomicService(this.documentFile, documentType);
            }

            // update feature is not active for the main time
            // return this.updateDocument(fileStorageId);
        }

        return null;
    }
}
